import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Button,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ImageIcon from "@mui/icons-material/Image";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { getOffersPublicAll } from "../../../services/publicPegasApi/MakePublicOrder";

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("ar-EG", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const OnlineOffersTables = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // IMAGE_BASE_URL مثل CustomerMenuMainPage
  const subdomain = window.location.hostname.split(".")[0];
  const IMAGE_BASE_URL = subdomain ? `https://${subdomain}.restocash.app/` : "";
  const placeholderImg = "https://via.placeholder.com/400x300?text=Offer";

  const getImageUrl = (path, placeholder = placeholderImg) => {
    if (!path) return placeholder;
    if (typeof path === "string" && (path.startsWith("http://") || path.startsWith("https://"))) {
      return path;
    }
    const cleanPath = String(path).replace(/^\/+/, "");
    return IMAGE_BASE_URL ? `${IMAGE_BASE_URL.replace(/\/+$/, "")}/${cleanPath}` : placeholder;
  };

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getOffersPublicAll();
      const list = res?.data ?? res;
      setOffers(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.message || String(err) || "فشل في جلب العروض");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleOpenDetails = (offer) => {
    setSelectedOffer(offer);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedOffer(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
        }}
      >
        <Box
          sx={{
            width: 70,
            height: 70,
            borderRadius: "50%",
            bgcolor: "#fffbe6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            boxShadow: "0 4px 20px rgba(255,179,0,0.15)",
          }}
        >
          <CircularProgress size={36} sx={{ color: "#ffb300" }} />
        </Box>
        <Typography sx={{ color: "#ffb300", fontWeight: 700, fontSize: 16 }}>
          جاري تحميل العروض...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Paper
          sx={{
            p: 3,
            textAlign: "center",
            borderRadius: 3,
            bgcolor: "#fff5f5",
            border: "2px dashed #ff5252",
          }}
        >
          <Typography color="error" sx={{ mb: 2, fontWeight: 700 }}>
            ⚠️ {error}
          </Typography>
          <Button
            variant="contained"
            color="error"
            startIcon={<RefreshIcon />}
            onClick={load}
          >
            إعادة المحاولة
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        fontFamily: "Cairo, Arial, sans-serif",
        bgcolor: "#fafafa",
        minHeight: "100%",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          p: { xs: 1.5, sm: 2 },
          bgcolor: "#fffbe6",
          borderRadius: 2,
          boxShadow: "0 2px 12px rgba(255,179,0,0.08)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocalOfferIcon sx={{ fontSize: 28, color: "#ffb300" }} />
          <Typography
            variant="h6"
            sx={{ fontWeight: 900, color: "#333", fontSize: { xs: 18, sm: 20 } }}
          >
            العروض الخاصة
          </Typography>
        </Box>
        <IconButton
          onClick={load}
          sx={{
            bgcolor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            "&:hover": { bgcolor: "#ffe0b2", transform: "rotate(180deg)" },
            transition: "all 0.3s",
          }}
        >
          <RefreshIcon sx={{ color: "#ffb300" }} />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        {offers.length === 0 && (
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 4,
                textAlign: "center",
                borderRadius: 3,
                bgcolor: "#fff",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              }}
            >
              <LocalOfferIcon sx={{ fontSize: 64, color: "#e0e0e0", mb: 2 }} />
              <Typography
                color="text.secondary"
                sx={{ fontWeight: 600, fontSize: 16 }}
              >
                لا توجد عروض حالياً
              </Typography>
            </Paper>
          </Grid>
        )}

        {offers.map((offer) => (
          <Grid key={offer.id} item xs={12}>
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                gap: { xs: 1.25, sm: 1.5 },
                p: { xs: 1.5, sm: 2 },
                alignItems: "flex-start",
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "#fff",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                border: "2px solid #ffe0b2",
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 32px rgba(255,179,0,0.15)",
                  borderColor: "#ffb300",
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: 90, sm: 110 },
                  height: { xs: 90, sm: 110 },
                  flexShrink: 0,
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "3px solid #ffe0b2",
                  position: "relative",
                }}
              >
                {offer.image ? (
                  <>
                    <img
                      src={getImageUrl(offer.image)}
                      alt={offer.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    {offer.status === "active" && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "#4caf50",
                          borderRadius: "50%",
                          width: 20,
                          height: 20,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(76,175,80,0.4)",
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#fff" }} />
                      </Box>
                    )}
                  </>
                ) : (
                  <ImageIcon sx={{ fontSize: 48, color: "#e0e0e0" }} />
                )}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        fontSize: { xs: 16, sm: 18 },
                        color: "#333",
                        mb: 0.25,
                        lineHeight: 1.2,
                      }}
                    >
                      {offer.name}
                    </Typography>
                    {offer.name_tage && (
                      <Chip
                        label={offer.name_tage}
                        size="small"
                        sx={{
                          bgcolor: "#ffe0b2",
                          color: "#ff6f00",
                          fontWeight: 700,
                          fontSize: 11,
                          height: 20,
                        }}
                      />
                    )}
                  </Box>

                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        fontSize: { xs: 18, sm: 20 },
                        color: "#ffb300",
                        lineHeight: 1,
                      }}
                    >
                      {(offer.total_price ?? offer.totalPrice ?? 0).toFixed
                        ? (Number(offer.total_price || 0)).toFixed(2)
                        : offer.total_price}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#999", fontWeight: 600, fontSize: 11 }}
                    >
                      جنية 
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  <Chip
                    icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}
                    label={formatDate(offer.start_date)}
                    size="small"
                    sx={{ bgcolor: "#e3f2fd", fontWeight: 600, fontSize: 11 }}
                  />
                  <Chip
                    icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}
                    label={formatDate(offer.end_date)}
                    size="small"
                    sx={{ bgcolor: "#fce4ec", fontWeight: 600, fontSize: 11 }}
                  />
                  <Chip
                    label={`${offer.items_number ?? offer.itemsNumber ?? (Array.isArray(offer.itmes) ? offer.itmes.length : (offer.items?.length ?? 0))} صنف`}
                    size="small"
                    sx={{ bgcolor: "#f3e5f5", fontWeight: 700, fontSize: 11 }}
                  />
                </Box>

                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800, mb: 0.5, color: "#555", fontSize: 13 }}
                  >
                    🍽️ الأصناف:
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {(offer.itmes || offer.items || []).slice(0, 6).map((it) => (
                      <Chip
                        key={it.id || `${it.product_id}-${it.product_name}`}
                        label={`${it.product_name || it.name || "غير معروف"} ×${it.quantity ?? it.qty ?? 1}`}
                        size="small"
                        sx={{
                          bgcolor: "#fff",
                          fontWeight: 700,
                          fontSize: 11,
                          border: "1.5px solid #e0e0e0",
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                <Box
                  sx={{
                    mt: 1.5,
                    display: "flex",
                    gap: 1,
                    flexDirection: isXs ? "column" : "row",
                  }}
                >
                  {/* <Button
                    size="small"
                    variant="contained"
                    sx={{
                      fontWeight: 900,
                      bgcolor: "#ffb300",
                      color: "#fff",
                      py: 1,
                      borderRadius: 2,
                      boxShadow: "0 4px 12px rgba(255,179,0,0.25)",
                      "&:hover": {
                        bgcolor: "#ff8f00",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(255,179,0,0.35)",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    🛒 اطلب الآن
                  </Button> */}
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth={isXs}
                    onClick={() => handleOpenDetails(offer)}
                    sx={{
                      fontWeight: 700,
                      borderColor: "#ffb300",
                      color: "#ffb300",
                      py: 1,
                      borderRadius: 2,
                      borderWidth: 2,
                      "&:hover": {
                        bgcolor: "#fffbe6",
                        borderColor: "#ff8f00",
                        borderWidth: 2,
                      },
                    }}
                  >
                    تفاصيل
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        fullScreen={isXs}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: isXs ? 0 : 3,
            maxHeight: "90vh",
            bgcolor: "#fafafa",
          },
        }}
      >
        {selectedOffer && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "#fff",
                borderBottom: "1px solid #e0e0e0",
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocalOfferIcon sx={{ fontSize: 28, color: "#ff9800" }} />
                <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, sm: 20 } }}>
                  {selectedOffer.name}
                </Typography>
              </Box>
              <IconButton 
                onClick={handleCloseDetails} 
                size="small"
                sx={{
                  bgcolor: "#f5f5f5",
                  "&:hover": { bgcolor: "#e0e0e0" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#fafafa" }}>
              {/* Offer Image */}
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 200, sm: 280 },
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2,
                  bgcolor: "#fff",
                  border: "2px solid #e0e0e0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                {selectedOffer.image ? (
                  <img
                    src={getImageUrl(selectedOffer.image)}
                    alt={selectedOffer.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <ImageIcon sx={{ fontSize: 80, color: "#bdbdbd" }} />
                )}
              </Box>

              {/* Offer Details */}
              <Box sx={{ mb: 2, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: "#666" }}>السعر الإجمالي:</Typography>
                  <Typography sx={{ fontWeight: 900, fontSize: 20, color: "#ff9800" }}>
                    {(selectedOffer.total_price ?? 0).toFixed(2)} جنية
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  <Chip
                    icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
                    label={`من: ${formatDate(selectedOffer.start_date)}`}
                    size="small"
                    sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }}
                  />
                  <Chip
                    icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
                    label={`إلى: ${formatDate(selectedOffer.end_date)}`}
                    size="small"
                    sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600 }}
                  />
                  <Chip
                    label={selectedOffer.status === "active" ? "نشط" : "غير نشط"}
                    size="small"
                    color={selectedOffer.status === "active" ? "success" : "default"}
                    sx={{ fontWeight: 700 }}
                  />
                </Box>

                {selectedOffer.description && (
                  <Box sx={{ mb: 0, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                    <Typography sx={{ fontWeight: 700, mb: 0.5, color: "#666" }}>الوصف:</Typography>
                    <Typography variant="body2" sx={{ color: "#757575" }}>
                      {selectedOffer.description}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2, borderColor: "#e0e0e0" }} />

              {/* Items List */}
              <Typography sx={{ fontWeight: 800, mb: 1.5, fontSize: 16, color: "#424242", px: 1 }}>
                🍽️ الأصناف المتضمنة ({(selectedOffer.itmes || selectedOffer.items || []).length}):
              </Typography>
              <List sx={{ bgcolor: "#fff", borderRadius: 2, p: 0, border: "1px solid #e0e0e0" }}>
                {(selectedOffer.itmes || selectedOffer.items || []).map((item, idx) => (
                  <React.Fragment key={item.id || idx}>
                    <ListItem
                      sx={{
                        py: 1.5,
                        px: 2,
                        "&:hover": { bgcolor: "#f5f5f5" },
                        transition: "all 0.2s",
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={getImageUrl(item.product_image)}
                          sx={{
                            width: 56,
                            height: 56,
                            border: "2px solid #e0e0e0",
                            bgcolor: "#f5f5f5",
                          }}
                        >
                          <ImageIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#424242" }}>
                            {item.product_name || item.name || "غير معروف"}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: "#757575", fontSize: 13 }}>
                              الكمية: {item.quantity ?? item.qty ?? 1} · السعر: {(item.price ?? 0).toFixed(2)} جنية
                            </Typography>
                            {item.note && (
                              <Typography variant="caption" sx={{ color: "#9e9e9e", fontStyle: "italic" }}>
                                ملاحظة: {item.note}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <Typography sx={{ fontWeight: 900, fontSize: 16, color: "#ff9800" }}>
                        {((item.price ?? 0) * (item.quantity ?? 1)).toFixed(2)}
                      </Typography>
                    </ListItem>
                    {idx < (selectedOffer.itmes || selectedOffer.items || []).length - 1 && <Divider sx={{ borderColor: "#f5f5f5" }} />}
                  </React.Fragment>
                ))}
              </List>
            </DialogContent>

            <DialogActions
              sx={{
                p: 2,
                bgcolor: "#fff",
                borderTop: "1px solid #e0e0e0",
                justifyContent: "space-between",
                flexDirection: isXs ? "column" : "row",
                gap: 1,
              }}
            >
              <Button
                onClick={handleCloseDetails}
                variant="outlined"
                fullWidth={isXs}
                sx={{
                  fontWeight: 700,
                  borderColor: "#bdbdbd",
                  color: "#757575",
                  "&:hover": { borderColor: "#757575", bgcolor: "#fafafa" },
                }}
              >
                إغلاق
              </Button>
              {/* <Button
                variant="contained"
                fullWidth={isXs}
                startIcon={<ShoppingCartIcon />}
                sx={{
                  fontWeight: 900,
                  bgcolor: "#ff9800",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(255,152,0,0.3)",
                  "&:hover": {
                    bgcolor: "#f57c00",
                    boxShadow: "0 4px 12px rgba(255,152,0,0.4)",
                  },
                }}
              >
                اطلب الآن
              </Button> */}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default OnlineOffersTables;
